import { useState, useEffect, useMemo, useRef } from 'react';
import { History, ChevronDown, ChevronUp, Wrench } from 'lucide-react';
import { useTranslations } from '../i18n';
import { FormLabel } from '../components/ui/FormLabel';
import { ImageUpload } from '../components/ui/ImageUpload';
import { supabase } from '../lib/supabase';
import { LogEntryItem } from '../components/cars/LogEntryItem';
import { NoteItem } from '../components/cars/NoteItem';
import { usePreferences } from '../contexts/PreferencesContext';
import { ImageViewer } from '../components/ui/ImageViewer';
import type { SparePart, SparePartFormData } from '../types/spareParts';

interface SparePartUsageFormProps {
    sparePart: SparePart; // The master record
    usage?: Partial<SparePartFormData>; // Existing combined data
    onChange: (usage: Partial<SparePartFormData>) => void;
    readOnly?: boolean;
    preferences?: {
        currency?: string;
        currency_symbol?: string;
        distance_unit?: string;
        volume_unit?: string;
    };
    slug: string;
    isAdminView?: boolean;
    currentExpenseId?: string;
    currentNoteId?: string;
}

export function SparePartUsageForm({
    sparePart,
    usage,
    onChange,
    readOnly = false,
    preferences: propPreferences,
    slug,
    isAdminView = false,
    currentExpenseId,
    currentNoteId
}: SparePartUsageFormProps) {
    const { t } = useTranslations();
    const { preferences: globalPreferences } = usePreferences();
    const currencySymbol = propPreferences?.currency_symbol || (globalPreferences as any)?.currency_symbol || '€';

    const [quantity, setQuantity] = useState(usage?.quantity?.toString() || '1');
    const [pricePerUnit, setPricePerUnit] = useState(usage?.price_per_unit?.toString() || '');
    const [comment, setComment] = useState(usage?.comment || '');
    const [isUsageExpanded, setIsUsageExpanded] = useState(false);
    const [linkedExpenses, setLinkedExpenses] = useState<any[]>([]);
    const [linkedNotes, setLinkedNotes] = useState<any[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [viewerImages, setViewerImages] = useState<string[]>([]);
    const [viewerIndex, setViewerIndex] = useState(0);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const isFirstRender = useRef(true);

    // Initial auto-expand
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
    }, [comment]);

    // Update parent when values change
    useEffect(() => {
        // Skip first run to avoid triggering parent's "hasChanges" on mount/expand
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        onChange({
            // Usage fields
            quantity: parseInt(quantity) || 1,
            price_per_unit: pricePerUnit ? parseFloat(pricePerUnit) : undefined,
            comment: comment.trim() || undefined,
            // Images (passed through from usage state if updated via ImageUpload)
            images: (usage as any)?.images || sparePart.images?.map(img => ({
                url: img.image_url,
                thumbnail: img.image_url_thumb,
                comment: img.comment || '',
                log_entry_id: img.log_entry_id,
                note_id: img.note_id
            }))
        });
    }, [quantity, pricePerUnit, comment, (usage as any)?.images]);

    useEffect(() => {
        if (isUsageExpanded && linkedExpenses.length === 0 && linkedNotes.length === 0) {
            loadLinkedRecords();
        }
    }, [isUsageExpanded]);

    const loadLinkedRecords = async () => {
        setLoadingHistory(true);
        try {
            const [expensesRes, notesRes] = await Promise.all([
                supabase
                    .from('log_entry_parts')
                    .select(`
                        log_entry_id,
                        quantity,
                        price_per_unit,
                        comment,
                        log_entries (
                            id,
                            date,
                            mileage,
                            title,
                            expenses (
                                description,
                                type,
                                amount
                            )
                        )
                    `)
                    .eq('spare_part_id', sparePart.id),
                supabase
                    .from('note_spare_parts')
                    .select(`
                        note_id,
                        quantity,
                        price_per_unit,
                        comment,
                        notes (
                            id,
                            title,
                            content,
                            created_at,
                            updated_at,
                            is_pinned,
                            is_public
                        )
                    `)
                    .eq('spare_part_id', sparePart.id)
            ]);

            if (!expensesRes.error && expensesRes.data) {
                setLinkedExpenses(expensesRes.data);
            }
            if (!notesRes.error && notesRes.data) {
                setLinkedNotes(notesRes.data);
            }
        } catch (err) {
            console.error('Error loading usage history:', err);
        } finally {
            setLoadingHistory(false);
        }
    };

    const effectivePreferences = useMemo(() => {
        const base = propPreferences || globalPreferences;
        if (!base) return null;
        return {
            ...base,
            currency_symbol: currencySymbol
        };
    }, [propPreferences, globalPreferences, currencySymbol]);

    return (
        <div className="space-y-4 pt-1">
            {/* Usage Stats / Toggle */}
            {sparePart.total_uses && sparePart.total_uses > 0 ? (
                <div className="bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl overflow-hidden">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setIsUsageExpanded(!isUsageExpanded);
                        }}
                        className="w-full px-4 py-2.5 flex items-center justify-between bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 transition-colors"
                    >
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-extrabold text-gray-500 dark:text-gray-400">
                            <History size={12} className="text-blue-500" />
                            <span>
                                {t.spareParts?.statistics?.usedTimes?.replace('{count}', String(sparePart.total_uses)) ||
                                    `Used ${sparePart.total_uses} times`}
                            </span>
                        </div>
                        {isUsageExpanded ? (
                            <ChevronUp size={14} className="text-gray-400" />
                        ) : (
                            <ChevronDown size={14} className="text-gray-400" />
                        )}
                    </button>

                    {/* Expanded Usage History */}
                    {isUsageExpanded && (
                        <div className="bg-white/30 dark:bg-black/20 border-t border-gray-100 dark:border-white/5 divide-y divide-gray-100 dark:divide-white/5">
                            {loadingHistory ? (
                                <div className="p-8 flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                                </div>
                            ) : (
                                <>
                                    {linkedExpenses.map((link, idx) => {
                                        const isCurrentMatch = link.log_entry_id === currentExpenseId;
                                        const recordId = link.log_entry_id || link.log_entries?.id;
                                        const recordImages = (usage?.images || sparePart.images || []).filter((img: any) => img.log_entry_id === recordId);
                                        const hasPrimaryDetails = (link.quantity && link.quantity > 1) || (link.price_per_unit && link.price_per_unit > 0) || link.comment;
                                        const hasDetails = hasPrimaryDetails || recordImages.length > 0;

                                        return (
                                            <div key={`exp-${idx}`} className="relative border-b border-gray-100 last:border-0 dark:border-white/5">
                                                <LogEntryItem
                                                    entry={{
                                                        ...link.log_entries,
                                                        title: isCurrentMatch ? ((t.expenses as any).thisExpense || 'Šis izdevums') : link.log_entries.title
                                                    }}
                                                    slug={slug}
                                                    effectivePreferences={effectivePreferences}
                                                    openInNewTab={!isCurrentMatch}
                                                    className={`!scale-100 !bg-transparent hover:!bg-white/40 dark:hover:!bg-white/5 ${isCurrentMatch ? 'border-l-4 border-blue-500' : ''}`}
                                                    onClick={isCurrentMatch ? () => setIsUsageExpanded(false) : undefined}
                                                />
                                                {hasDetails && (
                                                    <div className="ml-14 mr-4 mb-3">
                                                        <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-xl overflow-hidden">
                                                            {((link.quantity && link.quantity > 1) || (link.price_per_unit && link.price_per_unit > 0) || link.comment) && (
                                                                <div className="flex items-start gap-3 p-3">
                                                                    <div className="w-9 h-9 rounded-full bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                                        <Wrench size={16} className="text-orange-600 dark:text-orange-400" />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                                                                                {sparePart?.name}
                                                                            </span>
                                                                        </div>
                                                                        {(link.quantity || 1) > 1 && (
                                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                                ×{link.quantity}
                                                                            </span>
                                                                        )}
                                                                        {link.comment && (
                                                                            <p className="mt-0.5 text-[12px] italic text-gray-500 dark:text-gray-400 leading-relaxed">
                                                                                {link.comment}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    {link.price_per_unit > 0 && (
                                                                        <div className="text-right flex-shrink-0">
                                                                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                                                {link.price_per_unit.toFixed(2)} {effectivePreferences?.currency_symbol || '€'}
                                                                            </span>
                                                                            {(link.quantity || 1) > 1 && (
                                                                                <span className="block text-[11px] text-gray-400 dark:text-gray-500">
                                                                                    = {(link.price_per_unit * (link.quantity || 1)).toFixed(2)} {effectivePreferences?.currency_symbol || '€'}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {/* Images for this expense */}
                                                            {recordImages.length > 0 && (
                                                                <div className={`px-3 pb-3 ${hasPrimaryDetails ? '-mt-1' : ''} overflow-x-auto scrollbar-hide`}>
                                                                    <div className="flex gap-2 min-w-0">
                                                                        {recordImages.map((img: any, imgIdx: number) => (
                                                                            <button
                                                                                key={imgIdx}
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    setViewerImages(recordImages.map((i: any) => i.url));
                                                                                    setViewerIndex(imgIdx);
                                                                                }}
                                                                                className="relative w-14 h-14 rounded-lg overflow-hidden border border-gray-100 dark:border-white/10 flex-shrink-0 hover:border-blue-500 transition-colors shadow-sm active:scale-95"
                                                                            >
                                                                                <img
                                                                                    src={img.thumbnail || img.url}
                                                                                    alt=""
                                                                                    className="w-full h-full object-cover"
                                                                                />
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {linkedNotes.map((link, idx) => {
                                        const isCurrentMatch = link.note_id === currentNoteId;
                                        const recordId = link.note_id || link.notes?.id;
                                        const recordImages = (usage?.images || sparePart.images || []).filter((img: any) => img.note_id === recordId);
                                        const hasPrimaryDetails = (link.quantity && link.quantity > 1) || (link.price_per_unit && link.price_per_unit > 0) || link.comment;
                                        const hasDetails = hasPrimaryDetails || recordImages.length > 0;

                                        return (
                                            <div key={`note-${idx}`} className="hover:bg-white/40 dark:hover:bg-white/5 transition-colors relative border-b border-gray-100 last:border-0 dark:border-white/5">
                                                <NoteItem
                                                    note={isCurrentMatch ? {
                                                        ...link.notes,
                                                        title: (t.notes as any).thisNote || 'Šī piezīme',
                                                        updated_at: link.notes.updated_at || link.notes.created_at
                                                    } : {
                                                        ...link.notes,
                                                        updated_at: link.notes.updated_at || link.notes.created_at
                                                    }}
                                                    slug={slug}
                                                    isAdminView={isAdminView}
                                                    compact={true}
                                                    openInNewTab={!isCurrentMatch}
                                                    onClick={isCurrentMatch ? () => setIsUsageExpanded(false) : undefined}
                                                    className={isCurrentMatch ? 'border-l-4 border-blue-500' : ''}
                                                />
                                                {hasDetails && (
                                                    <div className="ml-14 mr-4 mb-3">
                                                        <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-xl overflow-hidden">
                                                            {((link.quantity && link.quantity > 1) || (link.price_per_unit && link.price_per_unit > 0) || link.comment) && (
                                                                <div className="flex items-start gap-3 p-3">
                                                                    <div className="w-9 h-9 rounded-full bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                                        <Wrench size={16} className="text-orange-600 dark:text-orange-400" />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                                                                                {sparePart?.name}
                                                                            </span>
                                                                        </div>
                                                                        {(link.quantity || 1) > 1 && (
                                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                                ×{link.quantity}
                                                                            </span>
                                                                        )}
                                                                        {link.comment && (
                                                                            <p className="mt-0.5 text-[12px] italic text-gray-500 dark:text-gray-400 leading-relaxed">
                                                                                {link.comment}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Images for this note */}
                                                            {recordImages.length > 0 && (
                                                                <div className={`px-3 pb-3 ${hasPrimaryDetails ? '-mt-1' : ''} overflow-x-auto scrollbar-hide`}>
                                                                    <div className="flex gap-2 min-w-0">
                                                                        {recordImages.map((img: any, imgIdx: number) => (
                                                                            <button
                                                                                key={imgIdx}
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    setViewerImages(recordImages.map((i: any) => i.url));
                                                                                    setViewerIndex(imgIdx);
                                                                                }}
                                                                                className="relative w-14 h-14 rounded-lg overflow-hidden border border-gray-100 dark:border-white/10 flex-shrink-0 hover:border-blue-500 transition-colors shadow-sm active:scale-95"
                                                                            >
                                                                                <img
                                                                                    src={img.thumbnail || img.url}
                                                                                    alt=""
                                                                                    className="w-full h-full object-cover"
                                                                                />
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    )}
                </div>
            ) : null}

            {/* Transaction-Specific Fields */}
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    {/* Quantity */}
                    <div>
                        <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary" isFilled={!!quantity}>
                            {t.spareParts?.usage?.quantity || 'Quantity'}
                        </FormLabel>
                        <input
                            type="number"
                            min="1"
                            step="1"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            disabled={readOnly}
                            placeholder="1"
                            className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500 font-primary"
                        />
                    </div>

                    {/* Price Per Unit */}
                    <div>
                        <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary" isFilled={!!pricePerUnit}>
                            {t.spareParts?.usage?.pricePerUnit || 'Price per unit'} ({currencySymbol})
                        </FormLabel>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={pricePerUnit}
                            onChange={(e) => setPricePerUnit(e.target.value)}
                            disabled={readOnly}
                            placeholder={sparePart.avg_price ? `${t.spareParts?.statistics?.avgPrice || 'Avg'}: ${currencySymbol}${sparePart.avg_price.toFixed(2)}` : '0.00'}
                            className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500 font-primary"
                        />
                    </div>
                </div>

                {/* Comment */}
                <div>
                    <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {t.spareParts?.usage?.comment || 'Comment'}
                    </FormLabel>
                    <textarea
                        ref={textareaRef}
                        value={comment}
                        onChange={(e) => {
                            setComment(e.target.value);
                            // Auto-expand
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        onFocus={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        disabled={readOnly}
                        rows={2}
                        placeholder={t.spareParts?.usage?.commentPlaceholder || 'e.g., Bought at AutoZone'}
                        className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500 overflow-y-auto"
                        style={{ minHeight: '80px', maxHeight: '200px' }}
                    />
                </div>

                {/* Images (Master Record) - Horizontal Scroll */}
                <div className="pt-2">
                    <ImageUpload
                        images={(usage as any)?.images || sparePart.images?.map(img => ({
                            url: img.image_url,
                            thumbnail: img.image_url_thumb,
                            comment: img.comment || '',
                            log_entry_id: img.log_entry_id,
                            note_id: img.note_id,
                            is_receipt: !!img.is_receipt
                        })) || []}
                        onChange={(images: any[]) => {
                            onChange({
                                // We send the updated images back to the parent
                                ...usage,
                                images: images
                            } as any);
                        }}
                        readOnly={readOnly}
                        horizontal={true}
                        maxImages={20}
                        folder="spare_parts"
                        showReceiptToggle={true}
                    />
                </div>
            </div>

            {viewerImages.length > 0 && (
                <ImageViewer
                    images={viewerImages}
                    initialIndex={viewerIndex}
                    onClose={() => setViewerImages([])}
                />
            )}
        </div>
    );
}
